import React from 'react';
import { styled } from '@abyss/ui/tools/styled';
import { Button } from 'src/common/Button';
import { useModel } from '@abyss/ui/form/hooks/useModel';
import { useForm } from '@abyss/ui/form/hooks/useForm';
import { storage } from '@abyss/ui/tools/storage';
import { config } from '@abyss/ui/tools/config';
import mapValues from 'lodash/mapValues';
import uniqWith from 'lodash/uniqWith';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { useAdminContext } from 'src/hooks/useAdminContext';

import { InternalUserForm } from './InternalUserForm';
import { FindForm } from './FindForm';

const ContinueButton = styled.div({
  margin: '20px 100px 20px 0',
  float: 'right',
});

const Table = styled.table({
  '& th': {
    fontWeight: 'bold',
  },
  '& th, & td': {
    minWidth: 200,
    textAlign: 'left',
  },
});

const Heading = styled.h3({
  marginTop: 20,
});

export const InternalUser = () => {
  const form = useForm();
  const emulationModel = useModel('emulation', {});
  const emulatedUser = emulationModel.emulatedUser || {};

  const [, setAdminContext] = useAdminContext({
    onCompleted: () => {
      const redirect = storage.session.get('redirect');
      window.location = decodeURIComponent(redirect || config('SDE_URL'));
    },
  });

  const savePreference = data => {
    const history = storage.local.get('internal-user:history') || [];
    const { uuid, ohid, ...provider } = data;

    const values = mapValues(provider, value => {
      if (typeof value === 'string') {
        return value.toUpperCase();
      }
      if (typeof value === 'undefined') {
        return '';
      }
      return value;
    });

    values.uuid = uuid;
    values.optumId = ohid;
    values.ohid = ohid;
    values.corporateProviderLastName = values.corporateProviderName;
    values.servicingProviderName = [
      typeof data.servicingProviderFirstName === 'string'
        ? data.servicingProviderFirstName.toUpperCase()
        : data.servicingProviderFirstName,
      typeof data.servicingProviderLastName === 'string'
        ? data.servicingProviderLastName.toUpperCase()
        : data.servicingProviderLastName,
    ]
      .filter(n => n)
      .join(' ');

    const newValueToAdd = omit(values, ['label', 'value']);

    const historyWithNewValue = [newValueToAdd].concat(history);
    const filteredHistory = uniqWith(historyWithNewValue, isEqual);
    storage.local.set('internal-user:history', filteredHistory);
  };

  const handleContinue = () => {
    form.submit('internalPreference', data => {
      const { uuid, ohid, ...provider } = data;

      savePreference(data);
      setAdminContext({
        variables: {
          role: 'internal-user',
          id: uuid,
          username: ohid,
          provider,
        },
      });
    });
  };

  return (
    <React.Fragment>
      <p>
        The ID you signed in with has Internal User Access. Please enter the
        MPIN of the user you want to sign in as.
      </p>
      <FindForm />
      <If condition={emulatedUser.optum_uuid}>
        <Heading>Confirm User</Heading>
        <p>
          Below is the name and email address associated with the MPIN you
          entered. If correct, click continue.
        </p>
        <Table>
          <tbody>
            <tr>
              <th>Name</th>
              <th>Email Address</th>
            </tr>
            <tr>
              <td>
                {emulatedUser.firstName} {emulatedUser.lastName}
              </td>
              <td>{emulatedUser.email}</td>
            </tr>
          </tbody>
        </Table>
      </If>
      <InternalUserForm uuid={emulatedUser.optum_uuid} />
      <ContinueButton>
        <Button
          data-testid="continue-button"
          data-cy="continue-button"
          size="size700"
          onClick={handleContinue}
        >
          Continue
        </Button>
      </ContinueButton>
    </React.Fragment>
  );
};
